import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Position,
  type Edge,
  type Node as FlowNode,
} from 'reactflow';
import 'reactflow/dist/style.css';

export type ChatArtifact =
  | MetricCardsArtifact
  | CartesianChartArtifact
  | PieChartArtifact
  | TableArtifact
  | CalloutArtifact
  | JsonArtifact
  | TimelineArtifact
  | FlowGraphArtifact;

export type MetricCardsArtifact = {
  type: 'metric_cards';
  title?: string;
  metrics?: Array<{
    label: string;
    value: string | number;
    caption?: string;
    color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  }>;
};

export type CartesianChartArtifact = {
  type: 'bar_chart' | 'line_chart' | 'area_chart';
  title?: string;
  subtitle?: string;
  data?: Array<Record<string, any>>;
  xKey?: string;
  series?: Array<{
    key: string;
    label?: string;
    color?: string;
    stackId?: string;
  }>;
  height?: number;
};

export type PieChartArtifact = {
  type: 'pie_chart';
  title?: string;
  subtitle?: string;
  data?: Array<Record<string, any>>;
  nameKey?: string;
  valueKey?: string;
  height?: number;
};

export type TableArtifact = {
  type: 'table';
  title?: string;
  subtitle?: string;
  columns?: Array<{
    key: string;
    label?: string;
    width?: number | string;
  }>;
  rows?: Array<Record<string, any>>;
  maxRows?: number;
};

export type CalloutArtifact = {
  type: 'callout';
  severity?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message?: string;
};

export type JsonArtifact = {
  type: 'json';
  title?: string;
  value?: any;
  maxHeight?: number;
};

export type TimelineArtifact = {
  type: 'timeline';
  title?: string;
  subtitle?: string;
  items?: Array<{
    title?: string;
    timestamp?: string;
    status?: string;
    description?: string;
    metadata?: Record<string, any>;
  }>;
};

export type FlowGraphArtifact = {
  type: 'flow_graph';
  title?: string;
  subtitle?: string;
  height?: number;
  nodes?: Array<{
    id: string;
    label?: string;
    subtitle?: string;
    status?: string;
    type?: string;
    x?: number;
    y?: number;
    data?: Record<string, any>;
  }>;
  edges?: Array<{
    id?: string;
    source: string;
    target: string;
    label?: string;
    animated?: boolean;
    type?: string;
  }>;
};

type Props = {
  artifacts?: any[] | null;
};

const DEFAULT_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#d32f2f',
  '#0288d1',
  '#7b1fa2',
  '#388e3c',
  '#f57c00',
  '#455a64',
];

const SUPPORTED_ARTIFACT_TYPES = new Set([
  'metric_cards',
  'bar_chart',
  'line_chart',
  'area_chart',
  'pie_chart',
  'table',
  'callout',
  'json',
  'timeline',
  'flow_graph',
]);

function isObject(value: any): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function titleize(value: any): string {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatValue(value: any): string {
  if (value === null || value === undefined || value === '') return '-';

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function truncate(value: any, max = 180): string {
  const text = formatValue(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3).trim()}...`;
}

function safeJsonStringify(value: any): string {
  if (value === null || value === undefined || value === '') return '-';

  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// The backend metadata-compaction layer (toolbox_ai chat/history/MCP) can
// replace a long leaf value with a { type, chars, preview, truncated } sentinel
// (or the { _truncated, ... } list/dict sentinels). Those objects can survive as
// artifact field values pulled out of chat message metadata; rendering one
// directly as a JSX child triggers React error #31.
function isCompactionPreview(value: any): value is { preview: string } {
  return (
    isObject(value) &&
    typeof value.preview === 'string' &&
    'type' in value &&
    'chars' in value &&
    'truncated' in value
  );
}

// Coerces any metadata leaf value into a safe React text child. Never returns
// an object, so it cannot trigger React error #31.
function renderText(value: any): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (isCompactionPreview(value)) return value.preview;

  return formatValue(value);
}

function statusColor(value?: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const normalized = String(value || '').toLowerCase();

  if (['success', 'succeeded', 'complete', 'completed', 'healthy', 'active', 'passed', 'publishable'].includes(normalized)) {
    return 'success';
  }

  if (['pending', 'running', 'processing', 'queued', 'warning', 'needs_review', 'degraded'].includes(normalized)) {
    return 'warning';
  }

  if (['failed', 'failure', 'error', 'unhealthy', 'cancelled', 'canceled', 'validation_failed'].includes(normalized)) {
    return 'error';
  }

  if (['info', 'unknown'].includes(normalized)) {
    return 'info';
  }

  return 'default';
}

function normalizeArtifacts(input: any[] | null | undefined): ChatArtifact[] {
  if (!Array.isArray(input)) return [];

  return input
    .filter((item) => isObject(item))
    .filter((item) => SUPPORTED_ARTIFACT_TYPES.has(String(item.type || '')))
    .map((item) => item as ChatArtifact);
}

function artifactKey(artifact: ChatArtifact, index: number): string {
  return `${artifact.type}-${artifact.title || 'artifact'}-${index}`;
}

const ArtifactHeader: React.FC<{
  title?: string;
  subtitle?: string;
}> = ({ title, subtitle }) => {
  if (!title && !subtitle) return null;

  return (
    <Box mb={1.25}>
      {title ? (
        <Typography variant="subtitle2" fontWeight={700}>
          {renderText(title)}
        </Typography>
      ) : null}

      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {renderText(subtitle)}
        </Typography>
      ) : null}
    </Box>
  );
};

const MetricCardsRenderer: React.FC<{ artifact: MetricCardsArtifact }> = ({ artifact }) => {
  const metrics = Array.isArray(artifact.metrics) ? artifact.metrics : [];

  if (!metrics.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} />

      <Grid container spacing={1.25}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={`${metric.label}-${index}`}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    {renderText(metric.label)}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" fontWeight={700}>
                      {renderText(metric.value)}
                    </Typography>

                    {metric.color ? (
                      <Chip
                        size="small"
                        color={metric.color}
                        variant="outlined"
                        label={metric.color}
                      />
                    ) : null}
                  </Stack>

                  {metric.caption ? (
                    <Typography variant="caption" color="text.secondary">
                      {renderText(metric.caption)}
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

const CartesianChartRenderer: React.FC<{ artifact: CartesianChartArtifact }> = ({ artifact }) => {
  const data = Array.isArray(artifact.data) ? artifact.data : [];
  const series = Array.isArray(artifact.series) ? artifact.series : [];
  const xKey = artifact.xKey || 'name';
  const height = artifact.height || 300;

  if (!data.length || !series.length) {
    return (
      <Alert severity="info">
        Chart artifact did not include enough data to render.
      </Alert>
    );
  }

  const ChartComponent =
    artifact.type === 'line_chart'
      ? LineChart
      : artifact.type === 'area_chart'
        ? AreaChart
        : BarChart;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} subtitle={artifact.subtitle} />

      <Box sx={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ChartComponent data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} />
            <YAxis />
            <Tooltip />
            <Legend />

            {series.map((item, index) => {
              const color = item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

              if (artifact.type === 'line_chart') {
                return (
                  <Line
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label || titleize(item.key)}
                    stroke={color}
                    strokeWidth={2}
                    dot
                  />
                );
              }

              if (artifact.type === 'area_chart') {
                return (
                  <Area
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label || titleize(item.key)}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.18}
                    stackId={item.stackId}
                  />
                );
              }

              return (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  name={item.label || titleize(item.key)}
                  fill={color}
                  stackId={item.stackId}
                />
              );
            })}
          </ChartComponent>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const PieChartRenderer: React.FC<{ artifact: PieChartArtifact }> = ({ artifact }) => {
  const data = Array.isArray(artifact.data) ? artifact.data : [];
  const nameKey = artifact.nameKey || 'name';
  const valueKey = artifact.valueKey || 'value';
  const height = artifact.height || 300;

  if (!data.length) {
    return (
      <Alert severity="info">
        Pie chart artifact did not include data.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} subtitle={artifact.subtitle} />

      <Box sx={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip />
            <Legend />

            <Pie
              data={data}
              dataKey={valueKey}
              nameKey={nameKey}
              outerRadius={95}
              label
            >
              {data.map((_entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const TableRenderer: React.FC<{ artifact: TableArtifact }> = ({ artifact }) => {
  const rows = Array.isArray(artifact.rows) ? artifact.rows : [];
  const maxRows = artifact.maxRows || 25;
  const displayedRows = rows.slice(0, maxRows);

  if (!displayedRows.length) {
    return (
      <Alert severity="info">
        Table artifact did not include rows.
      </Alert>
    );
  }

  const columns =
    Array.isArray(artifact.columns) && artifact.columns.length
      ? artifact.columns
      : Object.keys(displayedRows[0] || {})
        .slice(0, 8)
        .map((key) => ({ key, label: titleize(key) }));

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} subtitle={artifact.subtitle} />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: Math.max(640, columns.length * 140) }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ width: column.width }}>
                  {renderText(column.label || titleize(column.key))}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedRows.map((row, rowIndex) => (
              <TableRow key={row.id || rowIndex} hover>
                {columns.map((column) => (
                  <TableCell key={`${rowIndex}-${column.key}`}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {truncate(row[column.key], 220)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {rows.length > maxRows ? (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Showing first {maxRows} of {rows.length} rows.
        </Typography>
      ) : null}
    </Paper>
  );
};

const CalloutRenderer: React.FC<{ artifact: CalloutArtifact }> = ({ artifact }) => {
  return (
    <Alert severity={artifact.severity || 'info'}>
      {artifact.title ? (
        <Typography variant="subtitle2" fontWeight={700}>
          {renderText(artifact.title)}
        </Typography>
      ) : null}

      <Typography variant="body2">
        {renderText(artifact.message)}
      </Typography>
    </Alert>
  );
};

const JsonRenderer: React.FC<{ artifact: JsonArtifact }> = ({ artifact }) => {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title || 'JSON'} />

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          maxHeight: artifact.maxHeight || 360,
          overflow: 'auto',
          bgcolor: 'grey.50',
          borderRadius: 1,
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {safeJsonStringify(artifact.value)}
      </Box>
    </Paper>
  );
};

const TimelineRenderer: React.FC<{ artifact: TimelineArtifact }> = ({ artifact }) => {
  const items = Array.isArray(artifact.items) ? artifact.items : [];

  if (!items.length) {
    return (
      <Alert severity="info">
        Timeline artifact did not include events.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} subtitle={artifact.subtitle} />

      <Stack spacing={1.25}>
        {items.map((item, index) => (
          <Paper key={`${item.title || 'event'}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`#${index + 1}`} />

                {item.status ? (
                  <Chip
                    size="small"
                    color={statusColor(item.status)}
                    variant="outlined"
                    label={renderText(item.status)}
                  />
                ) : null}

                {item.timestamp ? (
                  <Typography variant="caption" color="text.secondary">
                    {renderText(item.timestamp)}
                  </Typography>
                ) : null}
              </Stack>

              <Typography variant="body2" fontWeight={700}>
                {renderText(item.title) || `Event ${index + 1}`}
              </Typography>

              {item.description ? (
                <Typography variant="body2" color="text.secondary">
                  {renderText(item.description)}
                </Typography>
              ) : null}

              {item.metadata && Object.keys(item.metadata).length ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {safeJsonStringify(item.metadata)}
                </Box>
              ) : null}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

function buildFlowNodesAndEdges(artifact: FlowGraphArtifact): {
  nodes: FlowNode[];
  edges: Edge[];
} {
  const rawNodes = Array.isArray(artifact.nodes) ? artifact.nodes : [];
  const rawEdges = Array.isArray(artifact.edges) ? artifact.edges : [];

  const incomingCount = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  rawNodes.forEach((node) => {
    incomingCount.set(node.id, 0);
    outgoing.set(node.id, []);
  });

  rawEdges.forEach((edge) => {
    incomingCount.set(edge.target, (incomingCount.get(edge.target) || 0) + 1);
    outgoing.set(edge.source, [...(outgoing.get(edge.source) || []), edge.target]);
  });

  const roots = rawNodes.filter((node) => (incomingCount.get(node.id) || 0) === 0);
  const levelById = new Map<string, number>();

  const queue = roots.length ? roots.map((node) => node.id) : rawNodes.slice(0, 1).map((node) => node.id);
  queue.forEach((id) => levelById.set(id, 0));

  while (queue.length) {
    const current = queue.shift() as string;
    const currentLevel = levelById.get(current) || 0;

    (outgoing.get(current) || []).forEach((target) => {
      const existingLevel = levelById.get(target);
      const nextLevel = currentLevel + 1;

      if (existingLevel === undefined || nextLevel > existingLevel) {
        levelById.set(target, nextLevel);
        queue.push(target);
      }
    });
  }

  rawNodes.forEach((node) => {
    if (!levelById.has(node.id)) {
      levelById.set(node.id, 0);
    }
  });

  const nodesByLevel = new Map<number, typeof rawNodes>();
  rawNodes.forEach((node) => {
    const level = levelById.get(node.id) || 0;
    nodesByLevel.set(level, [...(nodesByLevel.get(level) || []), node]);
  });

  const positionedNodes: FlowNode[] = rawNodes.map((node) => {
    const level = levelById.get(node.id) || 0;
    const levelNodes = nodesByLevel.get(level) || [];
    const indexInLevel = Math.max(0, levelNodes.findIndex((candidate) => candidate.id === node.id));

    const x = typeof node.x === 'number' ? node.x : level * 300;
    const y = typeof node.y === 'number' ? node.y : indexInLevel * 130;

    const color = statusColor(node.status);
    const borderColor =
      color === 'success'
        ? '#2e7d32'
        : color === 'warning'
          ? '#ed6c02'
          : color === 'error'
            ? '#d32f2f'
            : '#90a4ae';

    return {
      id: node.id,
      type: 'default',
      position: { x, y },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        label: (
          <Box sx={{ minWidth: 160, maxWidth: 220 }}>
            <Typography variant="body2" fontWeight={700}>
              {renderText(node.label || node.id)}
            </Typography>

            {node.subtitle ? (
              <Typography variant="caption" color="text.secondary">
                {renderText(node.subtitle)}
              </Typography>
            ) : null}

            {node.status ? (
              <Box mt={0.5}>
                <Chip
                  size="small"
                  color={color}
                  variant="outlined"
                  label={node.status}
                />
              </Box>
            ) : null}
          </Box>
        ),
      },
      style: {
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        padding: 8,
        background: '#fff',
      },
    };
  });

  const flowEdges: Edge[] = rawEdges.map((edge, index) => ({
    id: edge.id || `${edge.source}-${edge.target}-${index}`,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    animated: Boolean(edge.animated),
    type: edge.type || 'smoothstep',
  }));

  return {
    nodes: positionedNodes,
    edges: flowEdges,
  };
}

const FlowGraphRenderer: React.FC<{ artifact: FlowGraphArtifact }> = ({ artifact }) => {
  const { nodes, edges } = React.useMemo(() => buildFlowNodesAndEdges(artifact), [artifact]);
  const height = artifact.height || 380;

  if (!nodes.length) {
    return (
      <Alert severity="info">
        Flow graph artifact did not include nodes.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <ArtifactHeader title={artifact.title} subtitle={artifact.subtitle} />

      <Box
        sx={{
          height,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.50',
        }}
      >
        <ReactFlow nodes={nodes} edges={edges} fitView nodesDraggable={false}>
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </Box>
    </Paper>
  );
};

const ArtifactRenderer: React.FC<{ artifact: ChatArtifact }> = ({ artifact }) => {
  if (artifact.type === 'metric_cards') {
    return <MetricCardsRenderer artifact={artifact} />;
  }

  if (artifact.type === 'bar_chart' || artifact.type === 'line_chart' || artifact.type === 'area_chart') {
    return <CartesianChartRenderer artifact={artifact} />;
  }

  if (artifact.type === 'pie_chart') {
    return <PieChartRenderer artifact={artifact} />;
  }

  if (artifact.type === 'table') {
    return <TableRenderer artifact={artifact} />;
  }

  if (artifact.type === 'callout') {
    return <CalloutRenderer artifact={artifact} />;
  }

  if (artifact.type === 'json') {
    return <JsonRenderer artifact={artifact} />;
  }

  if (artifact.type === 'timeline') {
    return <TimelineRenderer artifact={artifact} />;
  }

  if (artifact.type === 'flow_graph') {
    return <FlowGraphRenderer artifact={artifact} />;
  }

  return null;
};

// Blast-radius guard: a render error in one artifact (e.g. a stray value React
// refuses to render) must degrade to a small notice, not throw up through the tree
// and unmount the whole chat view into a blank screen.
class ArtifactErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    // eslint-disable-next-line no-console
    console.error('ChatArtifactsRenderer: failed to render an artifact', error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Alert severity="warning" variant="outlined">
          This result could not be displayed.
        </Alert>
      );
    }
    return this.props.children;
  }
}

const ChatArtifactsRenderer: React.FC<Props> = ({ artifacts }) => {
  const normalizedArtifacts = normalizeArtifacts(artifacts);

  if (!normalizedArtifacts.length) return null;

  return (
    <Stack spacing={1.5} mt={1.5}>
      {normalizedArtifacts.map((artifact, index) => (
        <Box key={artifactKey(artifact, index)}>
          <ArtifactErrorBoundary>
            <ArtifactRenderer artifact={artifact} />
          </ArtifactErrorBoundary>
        </Box>
      ))}
    </Stack>
  );
};

export default ChatArtifactsRenderer;